import React, { Children, CSSProperties, useCallback, useRef, useState } from 'react'
import { colorSystem, fontSystem, zIndexSystem } from '@/styles/theme'
import styled, { css, CSSObject } from 'styled-components'
import { createPortal } from 'react-dom'

interface TooltipProps {
  width?: string
  whiteSpace?: CSSProperties['whiteSpace']
  label: string
  initShow?: boolean
  children?: React.ReactNode
  type?: 'top' | 'bottom'
  indicatorOnCenter?: boolean
}

function Tooltip({ width, whiteSpace, label, children, type = 'top', initShow = false, indicatorOnCenter = true }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(initShow)

  const [tooltipPos, setTooltipPos] = useState<CSSObject>({})
  const onSetTooltipProps = useCallback((options: CSSObject) => setTooltipPos((prev) => ({ ...prev, ...options })), [])

  const $target = useRef<HTMLDivElement>(null)
  const Child = useCallback(
    () =>
      React.cloneElement(Children.only(children) as React.ReactElement, {
        ref: $target,
        onMouseEnter: () => setIsVisible(true),
        onMouseLeave: () => setIsVisible(false),
      }),
    [children],
  )

  const $tooltipRef = useCallback(
    ($tooltip: HTMLDivElement | null) => {
      if (!isVisible || !$target.current || !$tooltip) return

      const targetRect = $target.current.getBoundingClientRect()
      const tooltipRect = $tooltip.getBoundingClientRect()

      onSetTooltipProps({
        position: 'fixed',
        top: type === 'bottom' ? `${targetRect.top + targetRect.height + 4}px` : `${targetRect.top - tooltipRect.height - 4}px`,
        left: `${targetRect.left + targetRect.width / 2 - tooltipRect.width / 2}px`,
        bottom: 'unset',
      })
    },
    [onSetTooltipProps, type, isVisible],
  )

  return !children ? null : (
    <>
      <Child />
      {isVisible &&
        createPortal(
          <TooltipContent
            $width={width}
            ref={$tooltipRef}
            $isVisible={isVisible}
            $type={type}
            style={tooltipPos}
            $whiteSpace={whiteSpace}
            $indicatorOnCenter={indicatorOnCenter}
          >
            {label}
          </TooltipContent>,
          document.getElementById('#root') ?? document.body,
        )}
    </>
  )
}

const TOOLTIP_HEIGHT = 34
const TOOLTIP_SPACE = 10

export const TooltipContent = styled.p<{
  $isVisible: boolean
  $type?: 'top' | 'bottom'
  $indicatorOnCenter?: boolean
  $width?: string
  $whiteSpace?: CSSProperties['whiteSpace']
}>`
  width: ${({ $width }) => $width};
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
  transition: opacity 0.2s ease-in-out;
  position: absolute;
  padding: 8px 12px;
  background-color: ${colorSystem.bg.common.dim};
  color: ${colorSystem.text.common.inverse};
  border-radius: 8px;
  white-space: ${({ $whiteSpace }) => $whiteSpace ?? 'nowrap'};
  z-index: ${zIndexSystem.modal + 1};
  ${fontSystem.Body_12_medium};

  ${({ $type }) =>
    $type === 'bottom'
      ? css`
          bottom: calc(-${TOOLTIP_HEIGHT}px - ${TOOLTIP_SPACE}px);
        `
      : css`
          top: calc(-${TOOLTIP_HEIGHT}px - ${TOOLTIP_SPACE}px);
        `}
  &::before {
    content: '';
    position: absolute;
    border-style: solid;

    ${({ $type }) =>
      $type === 'bottom'
        ? css`
            border-width: 0 4px 6px 4px;
            border-color: transparent transparent ${colorSystem.bg.common.dim} transparent; // bottom
            top: -5px;
          `
        : css`
            border-width: 6px 4px 0 4px;
            border-color: ${colorSystem.bg.common.dim} transparent transparent transparent; // top
            bottom: -5px;
          `}

    ${({ $indicatorOnCenter }) =>
      $indicatorOnCenter &&
      css`
        left: calc(50% - 4px);
      `}
  }
`

export default Tooltip
